import { useQuery } from "react-query";
import axios from "axios";
import useDataStore from "../state/DataStore";
import { AnnualReturnData, SavingsProfile } from "../types/SavingsProfile";
import { TableType } from "../data/TableType";

export default function useSavingsProfiles(pensionProductTypeId: number) {
  const { setSavingsProfiles, setSelectableCompanies } = useDataStore();

  function mapAnnualReturns(
    profile: SavingsProfile,
  ): Record<string, AnnualReturnData> {
    return {
      "3": {
        bruttoReturn: profile.bruttoReturns3yr,
        nettoReturn: profile.nettoReturns3yr,
      },
      "5": {
        bruttoReturn: profile.bruttoReturns5yr,
        nettoReturn: profile.nettoReturns5yr,
      },
      "7": {
        bruttoReturn: profile.bruttoReturns7yr,
        nettoReturn: profile.nettoReturns7yr,
      },
      "10": {
        bruttoReturn: profile.bruttoReturns10yr,
        nettoReturn: profile.nettoReturns10yr,
      },
    };
  }

  function processSavingsProfiles(rawProfiles: SavingsProfile[]) {
    return rawProfiles
      .filter(
        (profile) =>
          profile.pensionProducts && profile.pensionProducts.length > 0,
      )
      .map((profile) => ({
        ...profile,
        activePensionProduct: profile.pensionProducts[0],
        annualReturns: mapAnnualReturns(profile),
      }));
  }

  function extractCompanies(profiles: SavingsProfile[]) {
    const companiesSet = new Set<string>();

    for (const profile of profiles) {
      const products = profile.pensionProducts;

      const companyName = products[0]?.company?.name;
      if (companyName) {
        companiesSet.add(companyName);
      }
    }

    return companiesSet;
  }

  async function fetchSavingsProfiles(
    pensionProductTypeId: number,
  ): Promise<SavingsProfile[]> {
    const apiURL: string = process.env.REACT_APP_API_DATA_URL || "";

    const response = await axios.get(
      `${apiURL}/pension/profiles/product-type/${pensionProductTypeId}`,
    );

    return processSavingsProfiles(response.data as SavingsProfile[]);
  }

  return useQuery(
    ["savingsProfiles", pensionProductTypeId],
    () => fetchSavingsProfiles(pensionProductTypeId),
    {
      onSuccess: (profiles) => {
        setSavingsProfiles(profiles);
        setSelectableCompanies(
          extractCompanies(profiles),
          TableType.SAVINGSPROFILES,
        );
      },
      retry: 3,
      refetchOnWindowFocus: false,
    },
  );
}
